import { useCallback, useMemo } from 'react'

import { EditorPanelItem, ElementsPanelConfig } from '@editor/interfaces/editor'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { Cylinder, Polygon, ShapeKeyType } from '@editor/interfaces/shape'
import { isCylinderWithinBoundingBox } from '@editor/services/Shape'
import { findDiameterKeyByValue, meterToMillimeter, zeroPad } from '@editor/services/Util'
import { getVolumeBoundingBox, getVolumeEstimationItems } from '@editor/services/VolumeEstimation'
import { RootState, useAppDispatch } from '@editor/store/app'
import { toggleSelectedElementId } from '@editor/store/tool'
import { useSelector } from 'react-redux'
import { Box3 } from 'three'

import DiameterSelection from '../components/DiameterSelection'
import { deleteWorkingLinesAtIndex } from '../store'

const isParentVolume = (parent: EditorPanelItem['item']): parent is InspectionItem =>
  !!parent && 'item_type' in parent && parent.item_type === InspectionItemType.Volume

const useElementsPanel = (): ElementsPanelConfig => {
  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const shapes = useSelector((state: RootState) => state.shapes.shapes)
  const workingDistanceLabels = useSelector((state: RootState) => state.toolRebarDetection.workingLines)
  const isSaving = useSelector((state: RootState) => state.toolRebarDetection.isSaving)
  const permissionSet = useSelector((state: RootState) => state.editor.permissionSet)
  const userType = useSelector((state: RootState) => state.user.userType)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)

  // Permission check
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)

  // Derived
  const volumes = useMemo(() => getVolumeEstimationItems(inspectionItems), [inspectionItems])

  /**
   * Get bounding box of volumes
   */
  const volumeBbox: { [name: string]: Box3 } = useMemo(
    () =>
      volumes.reduce((prev, volume) => {
        const bbox = getVolumeBoundingBox(
          shapes.polygons?.filter((shape) => volume.shape_ids?.polygons?.includes(shape.shape_id)) as [
            Polygon,
            Polygon,
          ],
        )

        return {
          ...prev,
          [volume.inspection_item_id!]: bbox,
        }
      }, {}),
    [volumes, shapes],
  )

  /**
   * Sort rebar into volume if it's inside and outside of volume.
   * Map key is volume inspection item id while non-volume rebar is stored under 'non-volume'.
   */
  const rebars: { [name: string]: EditorPanelItem[] } = useMemo(() => {
    const rebarMap: { [name: string]: EditorPanelItem[] } = { 'non-volume': [] }

    // We generate panel items first to maintain index for naming
    const panelItems = shapes.cylinders.map<EditorPanelItem>((item, index) => ({
      key: `panel-rebar-${item.shape_id}`,
      displayName: `鉄筋${zeroPad(index + 1, 3)}`,
      name: `鉄筋${zeroPad(index + 1, 3)} (${item.diameter ? findDiameterKeyByValue(meterToMillimeter(item.diameter)) : 'D??'})`,
      item,
      order: item.order_in_inspection_group,
      isGrouped: !!item.inspection_group_id,
      isOrderable: true,
      onDelete: isAllowedToModify,
    }))

    // Sort cylinders into volume
    panelItems.forEach((item) => {
      Object.entries(volumeBbox).forEach(([volumeId, bbox]) => {
        if (isCylinderWithinBoundingBox(item.item as Cylinder, bbox)) {
          rebarMap[volumeId] = rebarMap[volumeId] || []
          rebarMap[volumeId].push(item)
          item.isOrderable = false // Disable ordering for items inside volume
        }
      })
    })

    // Any leftover cylinders are non-volume
    rebarMap['non-volume'] = panelItems.filter((cylinder) => !Object.values(rebarMap).flat().includes(cylinder))

    return rebarMap
  }, [volumeBbox, shapes, isAllowedToModify])

  return {
    getPanelItems: useCallback(
      (parent?: EditorPanelItem['item']) => {
        // Rebar is both a parent and a child depending on whether it's inside volume or not
        let cylinders: EditorPanelItem[] = []
        if (isParentVolume(parent)) {
          cylinders = rebars[parent.inspection_item_id!] || []
        } else if (!parent) {
          cylinders = rebars['non-volume']
        }

        if (!parent && workingDistanceLabels.length) {
          cylinders = cylinders.concat(
            workingDistanceLabels.map<EditorPanelItem>((label, index) => ({
              key: `panel-rebar-${label.id}`,
              name: `鉄筋${zeroPad(shapes.cylinders.length + index + 1, 3)}`,
              // displayName: `鉄筋${zeroPad(shapes.cylinders.length + index + 1, 3)} (${label.diameter ? findDiameterKeyByValue(label.diameter) : 'D??'})`,
              // FIXME: need a different indicator to change diameter
              displayName: selectedElementIds.includes(label.id)
                ? `鉄筋${zeroPad(shapes.cylinders.length + index + 1, 3)}`
                : `鉄筋${zeroPad(shapes.cylinders.length + index + 1, 3)} (${label.diameter ? findDiameterKeyByValue(label.diameter) : 'D??'})`,
              dropdown: selectedElementIds.includes(label.id) ? <DiameterSelection label={label} /> : undefined,
              item: label,
              shapeKey: ShapeKeyType.CYLINDER,
              isWorking: true,
              isSaving,
              onSelect: isSaving
                ? false
                : () => {
                    dispatch(toggleSelectedElementId(label.id))
                  },
              onDelete: isSaving
                ? false
                : () => {
                    document.dispatchEvent(
                      new CustomEvent<{ index: number }>('editor:tool:rebarDetection:delete', { detail: { index } }),
                    )
                    dispatch(deleteWorkingLinesAtIndex(index))
                  },
            })),
          )
        }

        if (cylinders.length) {
          return [
            {
              key: 'panel-rebar',
              name: `鉄筋${!parent ? ' (体積なし)' : ''}`,
              isVirtualContainer: true,
              isCountable: true,
              shapeKey: ShapeKeyType.CYLINDER,
              children: cylinders,
            },
          ]
        }

        return []
      },
      [rebars, workingDistanceLabels, isSaving, shapes, selectedElementIds, dispatch],
    ),
  }
}

export default useElementsPanel
